import { useCallback, useEffect, useRef, useState } from "react";
import { uploadToStorage } from "../api/client";
import { notify } from "../util/notify";

export type UploadPhase = "idle" | "uploading" | "processing" | "completed" | "failed" | "delayed";

export interface UploadState {
  phase: UploadPhase;
  fileName?: string;
  /** Percentage of the file that has reached the server */
  progress: number;
  /** How far along processing is, once the server has reported it, and what it is doing */
  processProgress?: number;
  stage?: string;
  message?: string;
  reportCount?: number;
}

type UploadRecord = {
  uploadId: string;
  filename: string;
  status: "queued" | "processing" | "completed" | "failed";
  message?: string;
  reportCount?: number;
  progress?: number;
  stage?: string;
  createdAt: string;
};

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** What is wrong with a file before anything is sent, or undefined when it is fine to upload */
export const validateReportFile = (file: Pick<File, "name" | "size">) => {
  if (!/\.xlsx$/i.test(file.name)) return "Choose an Excel (.xlsx) file.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > MAX_UPLOAD_BYTES) return `That file is too large. The limit is ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`;
  return undefined;
};

const readAsBinaryString = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsBinaryString(file);
  });

const IDLE: UploadState = { phase: "idle", progress: 0 };
export const POLL_INTERVAL_MS = 1000;
// Processing normally takes seconds; past this the page stops waiting (the upload itself is not cancelled)
export const MAX_WAIT_MS = 10 * 60 * 1000;
const UPLOAD_FAILED = "The upload failed. Check your connection and try again.";

type InvokeApi = (args: Record<string, unknown>) => Promise<any>;

/**
 * Uploads a report and follows it until it has been processed: the file is sent by the API, but it is merged into
 * the reports afterwards, in the background, so the page polls for the outcome.
 */
export const useReportUpload = ({
  invokeApi,
  scopeKey,
  enabled,
  onProcessed,
}: {
  invokeApi: InvokeApi;
  /** Changing this (another facility or payer) abandons whatever is being followed */
  scopeKey: string;
  enabled: boolean;
  onProcessed?: () => void;
}) => {
  const [state, setState] = useState<UploadState>(IDLE);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Identifies the poll loop that is allowed to update the page; bumping it retires every older loop
  const generation = useRef(0);
  const onProcessedRef = useRef(onProcessed);
  onProcessedRef.current = onProcessed;

  const stopWatching = useCallback(() => {
    generation.current += 1;
    clearTimeout(timer.current);
  }, []);

  const watch = useCallback(
    (uploadId: string, fileName: string, startedAt: number) => {
      generation.current += 1;
      const mine = generation.current;

      const poll = async () => {
        let record: UploadRecord | undefined;
        try {
          record = await invokeApi({ endpoint: "/getUploadStatus", data: { uploadId }, silent: true });
        } catch {
          // A missed poll is not a failed upload; ask again
        }
        if (mine !== generation.current) return;

        if (record?.status === "completed") {
          setState({ phase: "completed", fileName, progress: 100, reportCount: record.reportCount });
          notify.success(`${fileName} has been processed.`);
          onProcessedRef.current?.();
        } else if (record?.status === "failed") {
          const message = record.message || "The report could not be processed.";
          setState({ phase: "failed", fileName, progress: 100, message });
          notify.error(`Could not process ${fileName}: ${message}`, { duration: 8000 });
        } else if (Date.now() - startedAt > MAX_WAIT_MS) {
          setState({ phase: "delayed", fileName, progress: 100 });
        } else {
          const { progress, stage } = record ?? {};
          if (progress !== undefined) {
            // Never moves backwards: a retried merge starts its steps over
            setState((current) =>
              current.phase === "processing"
                ? { ...current, processProgress: Math.max(current.processProgress ?? 0, progress), stage }
                : current
            );
          }
          timer.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      };
      timer.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [invokeApi]
  );

  /** Resolves true once the file has been handed over (processing then continues in the background) */
  const start = useCallback(
    async (file: File) => {
      stopWatching();
      const problem = validateReportFile(file);
      if (problem) {
        setState({ phase: "failed", fileName: file.name, progress: 0, message: problem });
        return false;
      }

      setState({ phase: "uploading", fileName: file.name, progress: 0 });
      const onUploadProgress = ({ loaded, total }: { loaded: number; total?: number }) => {
        if (!total) return;
        const progress = Math.min(100, Math.round((loaded / total) * 100));
        setState((current) => (current.phase === "uploading" ? { ...current, progress } : current));
      };

      // The file goes straight to storage; the API only grants the permission
      const sendDirect = async () => {
        const target = await invokeApi({ endpoint: "/requestUpload", data: { fileName: file.name, fileSize: file.size } });
        await uploadToStorage(target, file, onUploadProgress);
        return target.uploadId as string | undefined;
      };
      // Older servers, or a browser that cannot reach storage directly: send the file through the API instead
      const sendThroughApi = async () => {
        const content = await readAsBinaryString(file);
        const response = await invokeApi({
          endpoint: "/uploadReport",
          data: { file: { name: file.name, type: file.type, content } },
          onUploadProgress,
        });
        return response?.uploadId as string | undefined;
      };

      try {
        let uploadId: string | undefined;
        try {
          uploadId = await sendDirect();
        } catch (error: any) {
          // An answer from the API that explains itself (not allowed, too large, ...) is final
          if (error?.response?.data?.description) throw error;
          setState({ phase: "uploading", fileName: file.name, progress: 0 });
          uploadId = await sendThroughApi();
        }
        if (!uploadId) {
          setState({ phase: "delayed", fileName: file.name, progress: 100 });
          return true;
        }
        setState({ phase: "processing", fileName: file.name, progress: 100 });
        watch(uploadId, file.name, Date.now());
        return true;
      } catch (error: any) {
        // invokeApi has already shown the reason in a toast; keep it on the page too
        const reason = error?.response?.data?.message;
        const message = typeof reason === "string" && !reason.startsWith("[") ? reason : UPLOAD_FAILED;
        setState({ phase: "failed", fileName: file.name, progress: 0, message });
        return false;
      }
    },
    [invokeApi, stopWatching, watch]
  );

  // Pick up an upload that is still being processed, e.g. after the page was reloaded
  useEffect(() => {
    setState(IDLE);
    if (!enabled) return;
    let cancelled = false;
    invokeApi({ endpoint: "/getUploadStatus", data: {}, silent: true })
      .then((record: UploadRecord | undefined) => {
        if (cancelled || !record?.uploadId) return;
        const startedAt = Date.parse(record.createdAt);
        const inProgress = record.status === "queued" || record.status === "processing";
        if (inProgress && Date.now() - startedAt < MAX_WAIT_MS) {
          setState({
            phase: "processing",
            fileName: record.filename,
            progress: 100,
            ...(record.progress !== undefined && { processProgress: record.progress, stage: record.stage }),
          });
          watch(record.uploadId, record.filename, startedAt);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stopWatching();
    };
  }, [scopeKey, enabled, invokeApi, watch, stopWatching]);

  const dismiss = useCallback(() => setState(IDLE), []);

  return { state, start, dismiss, busy: state.phase === "uploading" || state.phase === "processing" };
};
